import React, { Component } from 'react';
import { GridLoader } from 'react-spinners';
import cloneDeep from 'lodash/cloneDeep';

import FilesLoader from './components/files-loader';
import Thumbs from './components/thumbs';
import Inspector from './components/inspector/Inspector';
import { INSPECTOR_TABS, getInspectorTabSeo, getInspectorTabSlugFromPath } from './components/inspector/tabRoutes';
import { replaceExtension } from './helpers/filename';
import { loadImageFiles } from './batch/imageFiles';
import { beginEngineLoad, loadEngine } from './engine/loadEngine';
import { trackEvent } from './analytics';
import { getDownloadAnalytics, summarizeUploadedImages } from './analytics/features';

export default class App extends Component {
    state = {
        images: [],
        processingPreview: false,
        processingImages: false,
        processingId: '',
        loading: false,
        selected: [],
        cropped: [],
        crops: {},
        progress: 0,
        bgRemovalProgress: false,
        activeTabSlug: getInspectorTabSlugFromPath(window.location.pathname),
        behindLayersPreview: null,
        engineReady: false,
        engineLoading: false,
    };

    bgCache = new Map();
    inspectorApi = {};

    render() {
        const { images, loading, processingPreview, processingImages } = this.state;
        const hasImages = images.length > 0;
        const showFilesLoader = !(hasImages || loading);
        const showLoading = !showFilesLoader && loading;
        const showThumbs = hasImages && !loading;

        return (
            <div className="flex h-full flex-col desk:flex-row overflow-hidden">
                <div className="relative flex min-h-0 min-w-0 flex-1 flex-col bg-[#FAFAFA]">
                    <div className="relative flex min-h-0 flex-1 flex-col overflow-auto">
                        { showFilesLoader && (
                            <React.Fragment>
                                { this.renderFilesLoader() }
                                { this.renderInfo() }
                            </React.Fragment>
                        ) }
                        { showLoading && this.renderLoading() }
                        { showThumbs && this.renderThumbs() }
                        { this.renderEngineLoading() }
                        { processingPreview && this.renderProcessingPreview() }
                        { processingImages && this.renderProcessingImages() }
                    </div>
                </div>
                <div className="flex min-h-0 min-w-0 flex-1 flex-col border-t border-border-subtle bg-white desk:flex-[0_0_42%] desk:max-w-inspector desk:border-l desk:border-t-0">
                    <Inspector
                        hasImages={hasImages}
                        hasSelection={this.state.selected.length > 0}
                        selected={this.state.selected}
                        focusedImageId={this.state.selected[0] || null}
                        inspectorApi={this.inspectorApi}
                        onBackgroundLayersChange={this.onBackgroundLayersChange}
                        onFirstBehindLayerAdded={this.onFirstBehindLayerAdded}
                        allImageIds={images.map(image => image.id)}
                        onUpdate={this.onOptionsChange}
                        onDownload={this.onDownload}
                        onTabChange={this.onTabChange}
                        isProcessing={processingImages || processingPreview}
                    />
                </div>
            </div>
        );
    }

    renderFilesLoader() {
        return (
            <FilesLoader
                onStartLoading={this.startLoading}
                onComplete={this.onFilesReady}
                onError={this.onFilesLoadError}
            />
        );
    }

    renderInfo() {
        const seo = getInspectorTabSeo(this.state.activeTabSlug);

        return (
            <section className="mx-auto w-full max-w-2xl px-6 pb-16 pt-2 desk:px-10">
                <div className="space-y-8">
                    <div className="space-y-3">
                        <h1 className="text-headline-md text-on-surface">{ seo.heading }</h1>
                        <p className="text-body-md leading-relaxed text-secondary">
                            { seo.intro }
                        </p>
                    </div>
                    { this.state.activeTabSlug === 'background' && (
                        <div className="space-y-2">
                            <h2 className="text-base font-semibold text-on-surface">Text behind subject</h2>
                            <p className="text-body-md leading-relaxed text-secondary">
                                Enable Text/Image Behind Subject to place words or graphics between the background and the foreground.
                                Drag layers on the preview to position them, then watch them disappear naturally behind the subject.
                            </p>
                        </div>
                    ) }
                    { this.state.activeTabSlug === 'watermarks' && (
                        <div className="space-y-2">
                            <h2 className="text-base font-semibold text-on-surface">Text and logo watermarks</h2>
                            <p className="text-body-md leading-relaxed text-secondary">
                                Choose font, size, color, transparency, angle, and placement on top of your original images.
                                Drag image watermarks onto the position grid or use overlay mode for a full-frame mark.
                            </p>
                        </div>
                    ) }
                    { this.state.activeTabSlug === 'effects' && (
                        <div className="space-y-2">
                            <h2 className="text-base font-semibold text-on-surface">Tiles, cubes, squares, dots, and ASCII</h2>
                            <p className="text-body-md leading-relaxed text-secondary">
                                Rebuild each photo as a mosaic of solid tiles, shaded cubes, screen-like squares, shaded dots, or ASCII characters.
                                Choose a color palette, tile size, and style — then apply it across the whole batch in your browser.
                            </p>
                        </div>
                    ) }
                    { this.state.activeTabSlug === 'transform' && (
                        <div className="space-y-2">
                            <h2 className="text-base font-semibold text-on-surface">Resize and crop in bulk</h2>
                            <p className="text-body-md leading-relaxed text-secondary">
                                Set width and height in pixels, percent, or auto. Rotate, flip, zoom, and crop to a rectangle —
                                especially useful after rotation when you need clean rectangular output.
                            </p>
                        </div>
                    ) }
                    <nav aria-label="Picinez features" className="space-y-2">
                        <h2 className="text-base font-semibold text-on-surface">Explore batch image tools</h2>
                        <ul className="flex flex-wrap gap-x-4 gap-y-2 text-body-md">
                            { INSPECTOR_TABS.filter(tab => tab.slug !== 'misc' && tab.slug !== 'settings').map(tab => (
                                <li key={tab.slug}>
                                    <a
                                        className="text-primary hover:underline"
                                        href={`/${tab.slug}`}
                                    >
                                        { tab.label }
                                    </a>
                                </li>
                            )) }
                        </ul>
                    </nav>
                    <div className="space-y-2">
                        <h2 className="text-base font-semibold text-on-surface">Is it safe to open my images here?</h2>
                        <p className="text-body-md leading-relaxed text-secondary">
                            Absolutely! All processing happens in your browser, your images never leave your computer.
                        </p>
                    </div>
                    <div className="space-y-2">
                        <h2 className="text-base font-semibold text-on-surface">Something does not work / feature request</h2>
                        <p className="text-body-md leading-relaxed text-secondary">
                            This tool is a sister product of{' '}
                            <a className="text-primary hover:underline" href="https://gosignpdf.com">GoSignPDF</a>.
                            If you found a bug or want a new feature, please{' '}
                            <a className="text-primary hover:underline" href="http://gosignpdf.com/contact-us/">contact us</a>.
                        </p>
                    </div>
                </div>
            </section>
        );
    }

    renderLoading() {
        return (
            <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8">
                <GridLoader color="#00B3E0" loading />
                <span className="text-body-md text-secondary">Loading Images</span>
            </div>
        );
    }

    renderEngineLoading() {
        const { engineLoading, engineReady, images, loading, processingPreview, processingImages } = this.state;
        const show = images.length > 0
            && !loading
            && engineLoading
            && !engineReady
            && !processingPreview
            && !processingImages;

        if (!show) {
            return null;
        }

        return (
            <div className="absolute inset-x-0 top-0 z-10 flex items-center justify-center gap-2 bg-white/90 px-4 py-2">
                <GridLoader color="#00B3E0" loading size={8} />
                <span className="text-body-md text-secondary">Preparing editor tools…</span>
            </div>
        );
    }

    renderProcessingPreview() {
        const { progress, bgRemovalProgress } = this.state;
        return (
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-white/90 p-6">
                <GridLoader color="#00B3E0" loading />
                <span className="text-body-md font-medium text-on-surface">{ `Processing Preview (${progress} %)` }</span>
                { bgRemovalProgress && (
                    <span className="text-label-md text-secondary">{ `${bgRemovalProgress.stage} (${bgRemovalProgress.progress} %)` }</span>
                ) }
            </div>
        );
    }

    renderProcessingImages() {
        const { progress, bgRemovalProgress } = this.state;
        return (
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-white/90 p-6">
                <GridLoader color="#00B3E0" loading />
                { progress < 100 && (
                    <span className="text-body-md font-medium text-on-surface">{ `Processing Images (${progress} %)` }</span>
                ) }
                { bgRemovalProgress && (
                    <span className="text-label-md text-secondary">{ `${bgRemovalProgress.stage} (${bgRemovalProgress.progress} %)` }</span>
                ) }
                { progress >= 100 && (
                    <span className="text-body-md font-medium text-on-surface">Preparing Archive</span>
                ) }
            </div>
        );
    }

    renderThumbs() {
        const { images, processingId, selected, behindLayersPreview } = this.state;

        return (
            <Thumbs
                processingId={processingId}
                images={images}
                addImages={this.addImages}
                deleteImage={this.onDeleteImage}
                deleteSelected={this.onDeleteSelected}
                deleteAll={this.onDeleteAll}
                onSelectionChanged={this.updateSelected}
                onCropsChanged={this.updateCrops}
                behindSubjectEnabled={!!behindLayersPreview?.behindSubject}
                behindLayers={behindLayersPreview?.layers || []}
                focusedImageId={selected[0] || null}
                onLayerDrag={this.onLayerDrag}
            />
        );
    }

    startLoading = () => {
        this.setState({ loading: true });
        this.ensureEngineStarted();
    };

    ensureEngineStarted = () => {
        if (this.state.engineReady || this.state.engineLoading) {
            return;
        }

        this.setState({ engineLoading: true });
        beginEngineLoad()
            .then(() => {
                this.setState({ engineReady: true, engineLoading: false });
            })
            .catch((error) => {
                console.log('Engine load failed', error);
                this.setState({ engineLoading: false });
            });
    };

    getEngine = async () => {
        if (!this.state.engineReady && !this.state.engineLoading) {
            this.ensureEngineStarted();
        }

        return loadEngine();
    };

    prepareImageFiles = (files) => {
        return files.map(file => {
            file.previewThumb = cloneDeep(file).thumb;
            return file;
        });
    };

    onDeleteImage = (id) => {
        this.removeImagesById([id]);
    };

    onDeleteSelected = (ids) => {
        this.removeImagesById(ids);
    };

    removeImagesById = (ids) => {
        const idSet = new Set(ids);
        const { images, selected, cropped, crops } = this.state;
        const remainingCrops = { ...crops };
        ids.forEach(id => delete remainingCrops[id]);

        this.setState({
            images: images.filter(image => !idSet.has(image.id)),
            selected: selected.filter(id => !idSet.has(id)),
            cropped: cropped.filter(id => !idSet.has(id)),
            crops: remainingCrops,
        });
    };

    onFilesReady = (files, meta = {}) => {
        this.trackImagesUploaded(files, meta.method || 'unknown');
        this.ensureEngineStarted();
        this.setState({
            images: this.prepareImageFiles(files),
            loading: false,
        });
    };

    onFilesAdded = (files, meta = {}) => {
        this.trackImagesUploaded(files, meta.method || 'picker');
        this.ensureEngineStarted();
        this.setState(prevState => ({
            images: [...prevState.images, ...this.prepareImageFiles(files)],
            loading: false,
        }));
    };

    onFilesLoadError = (error, meta = {}) => {
        trackEvent('upload_failed', {
            method: meta.method || 'unknown',
            reason: error?.message || 'unknown',
        });
        this.setState({ loading: false });
    };

    addImages = (files) => {
        if (files.length > 0) {
            this.startLoading();
            loadImageFiles(files)
                .then(result => this.onFilesAdded(result, { method: 'picker' }))
                .catch(error => this.onFilesLoadError(error, { method: 'picker' }));
        }
    };

    trackImagesUploaded = (files, method) => {
        trackEvent('images_uploaded', {
            method,
            ...summarizeUploadedImages(files),
        });
    };

    onDeleteAll = () => {
        const { loading, processingImages, processingPreview } = this.state;
        if (!(loading || processingImages || processingPreview)) {
            this.setState({
                images: [],
                selected: [],
                cropped: [],
                crops: {},
            });
        }
    };

    onDownload = async (options, downloadScope = 'all') => {
        const { images, selected, cropped, crops } = this.state;
        if (images.length === 0) {
            return;
        }

        if (downloadScope === 'selected' && selected.length === 0) {
            return;
        }

        const scope = downloadScope === 'selected' ? selected : [];
        const imageCount = scope.length > 0 ? scope.length : images.length;
        const startedAt = Date.now();

        trackEvent('process_started', {
            scope: downloadScope,
            image_count: imageCount,
        });

        this.setState({ processingImages: true, bgRemovalProgress: false });

        try {
            const { processBatch, JSZip, saveAs } = await this.getEngine();
            const processedImages = await processBatch(
                images,
                options,
                {
                    scope,
                    crops,
                    cropped,
                    mode: 'download',
                    bgCache: this.bgCache,
                    focusedImageId: this.state.selected[0] || null,
                    onBgProgress: progress => this.setState({ bgRemovalProgress: progress }),
                },
                (id, pct) => this.setState({ processingId: id, progress: pct })
            );

            if (processedImages.length > 1) {
                const zip = new JSZip();
                const imgZip = zip.folder('images');
                processedImages.forEach(image => {
                    imgZip.file(
                        options.saveAs ? replaceExtension(image.name, options.saveAs) : image.name,
                        image.data.split('base64,')[1],
                        { base64: true }
                    );
                });
                const content = await zip.generateAsync({ type: 'blob' });
                saveAs(content, 'images.zip');
            } else if (processedImages.length === 1) {
                saveAs(
                    processedImages[0].data,
                    `new-${options.saveAs ? replaceExtension(processedImages[0].name, options.saveAs) : processedImages[0].name}`
                );
            }

            trackEvent('download_completed', {
                scope: downloadScope,
                output_type: processedImages.length > 1 ? 'zip' : 'single',
                image_count: processedImages.length,
                duration_ms: Date.now() - startedAt,
                ...getDownloadAnalytics(options, {
                    cropped,
                    imageCount: processedImages.length,
                }),
            });
        } catch (error) {
            trackEvent('process_failed', {
                scope: downloadScope,
                reason: error?.message || 'unknown',
            });
            throw error;
        } finally {
            this.setState({
                processingImages: false,
                processingId: '',
                progress: 100,
                bgRemovalProgress: false,
            });
        }
    };

    onOptionsChange = async (options) => {
        const { images } = this.state;
        if (images.length === 0) {
            return;
        }

        this.setState({ processingPreview: true, bgRemovalProgress: false });

        try {
            const previewState = await this.runPreviewProcessing(options);
            this.setState({
                ...previewState,
                processingPreview: false,
                processingId: '',
                bgRemovalProgress: false,
            });
        } catch (error) {
            console.log('Preview processing failed', error);
            this.setState({
                processingPreview: false,
                processingId: '',
                bgRemovalProgress: false,
            });
        }
    };

    onFirstBehindLayerAdded = async (options, imageIds) => {
        const { images } = this.state;
        if (images.length === 0 || imageIds.length === 0) {
            return;
        }

        this.setState({ processingPreview: true, bgRemovalProgress: false, progress: 0 });
        const segmentationStartedAt = Date.now();

        try {
            const { ensureBackgroundSegmentationForImages } = await this.getEngine();
            const targets = images.filter(image => imageIds.includes(image.id));
            await ensureBackgroundSegmentationForImages(
                targets,
                this.bgCache,
                progress => this.setState({ bgRemovalProgress: progress })
            );

            trackEvent('background_removed', {
                image_count: targets.length,
                duration_ms: Date.now() - segmentationStartedAt,
            });

            const previewState = await this.runPreviewProcessing(options);
            this.setState({
                ...previewState,
                processingPreview: false,
                processingId: '',
                bgRemovalProgress: false,
            });
        } catch (error) {
            console.log('First behind layer processing failed', error);
            this.setState({
                processingPreview: false,
                processingId: '',
                bgRemovalProgress: false,
            });
        }
    };

    runPreviewProcessing = async (options) => {
        const { images, selected, cropped, crops } = this.state;
        const { processBatch } = await this.getEngine();

        const results = await processBatch(
            images,
            options,
            {
                scope: selected,
                crops,
                cropped,
                mode: 'preview',
                bgCache: this.bgCache,
                focusedImageId: selected[0] || null,
                onBgProgress: progress => this.setState({ bgRemovalProgress: progress }),
            },
            (id, pct) => this.setState({ processingId: id, progress: pct })
        );

        return {
            images: images.map(image => {
                const result = results.find(item => item.id === image.id);
                if (!result) {
                    return image;
                }
                return {
                    ...image,
                    previewThumb: {
                        ...image.previewThumb,
                        src: result.thumb.data,
                    },
                    previewFull: result.preview ? {
                        src: result.preview.data,
                    } : null,
                };
            }),
            progress: 100,
        };
    };

    updateSelected = (selected) => {
        this.setState({ selected });
    };

    onTabChange = (activeTabSlug) => {
        this.setState({ activeTabSlug });
    };

    onBackgroundLayersChange = (behindLayersPreview) => {
        this.setState({ behindLayersPreview });
    };

    onLayerDrag = (layerId, x, y) => {
        if (this.inspectorApi.setLayerPosition) {
            this.inspectorApi.setLayerPosition(layerId, x, y);
        }
    };

    updateCrops = (cropped, crops) => {
        this.setState({ cropped, crops });
    };
}
